// src/components/ChargingStation/useChargingStation.ts
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Platform, InteractionManager } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMyDistanceFromCharger,
  getMyDrivingTime,
  updateSelectedCharger,
} from '../store/actions/charging-actions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getUserLocation } from '../components/common/utils/get-user-location';
import { getLocationFromIp } from '../components/common/utils/ip-address';
import { getDriverPlan } from '../store/actions/user-actions';
import { SendSchedule, setSchedule } from '../store/actions/scheduling-actions';

import { ChargerAttributes } from '../models/charger-model';
import { OrderAttributes } from '../models/order-model';
import { UserSessionAttributes } from '../models/user-session-model';
import { ActiveChargeAttributes } from '../models/active-charges-model';

import { useStripeWeb } from '../components/ChargingStation/hooks/useStripeWeb.web';
// Only import mobile hook on native platforms
// const useStripeMobile =
//   Platform.OS === 'web'
//     ? null
//     : require('../components/ChargingStation/hooks/useStripeMobile.native')
//         .useStripeMobile;
//@ts-ignore
import { useStripeMobile } from '../components/ChargingStation/hooks/useStripeMobile';
import { notificationService } from '../services/notification-service';
import { cancelOrder, updateOrderEmail } from '../services/orders-service';
import { checkoutCharger } from '../services/charger-service';
import { isSuccessful } from '../services/api-requests';
import * as Location from 'expo-location';
import RoleService from '../services/role-service';
import { BottomSheetModalRef } from '../components/BottomSheet';

/**
 * Main hook for ChargerStation screen.
 * All shared state & side-effects live here.
 * Stripe logic is delegated to useStripeWeb (web) or useStripeMobile (native).
 */
export function useChargingStation(navigation: any, route: any) {
  // ────────────────────────────────────────────────────────────────────────────────
  // Redux & session state
  // ────────────────────────────────────────────────────────────────────────────────
  const dispatch: AppDispatch = useDispatch();
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const selectedCharger: ChargerAttributes | null | undefined = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  );

  // ────────────────────────────────────────────────────────────────────────────────
  // Local state unrelated to Stripe
  // ────────────────────────────────────────────────────────────────────────────────
  const [email, setEmail] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState<string | null>(null);
  const [storedEmail, setStoredEmail] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [showCreateAccount, setShowCreateAccount] = useState<boolean>(false);

  const [myDistanceFromCharger, setMyDistanceFromCharger] =
    useState<string>('');
  const [myTimeFromCharger, setMyTimeFromCharger] = useState<string>('');

  const [myLocation, setMyLocation] =
    useState<Location.LocationObjectCoords | null>(
      route?.params.myLocation ?? null
    );

  // Schedule‐related
  const defaultStartTime = new Date();
  const defaultEndTime = new Date(
    defaultStartTime.getTime() + 12 * 60 * 60 * 1000
  );
  const [scheduleStartTime, setScheduleStartTime] =
    useState<Date>(defaultStartTime);
  const [scheduleEndTime, setScheduleEndTime] = useState<Date>(defaultEndTime);
  const [selectedMaxKwh, setSelectedMaxKwh] = useState<number | null>(null);
    const [scheduleSet, setScheduleSet] = useState<boolean>(false);

  // Tooltip
  const [showFeeTooltipVisible, setShowFeeTooltipVisible] =
    useState<boolean>(false);

  // Refs
  const scrollViewRef = useRef<any>(null);
  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);

  const renderCharger = useMemo(() => selectedCharger, [selectedCharger]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Delegate Stripe logic by platform:
  // ────────────────────────────────────────────────────────────────────────────────
  // onNewOrderCreated will let us keep track of the current Order on the screen
  const [webOrder, setWebOrder] = useState<OrderAttributes | null>(null);
  const onWebOrderCreated = (o: OrderAttributes) => setWebOrder(o);

  const {
    order: webStripeOrder,
    paymentData,
    paymentsDropdownOpen,
    loadError,
    chargerInUse,
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,
    stripeElements,
    reinitializeForNewOrder,
  } = useStripeWeb({
    selectedCharger: renderCharger,
    onNewOrderCreated: onWebOrderCreated,
  });

  const [mobileOrder, setMobileOrder] = useState<OrderAttributes | null>(null);
  const onMobileOrderCreated = (o: OrderAttributes) => setMobileOrder(o);

  // Which "order" we expose to the rest of the hook depends on platform
  const order = Platform.OS === 'web' ? webStripeOrder : mobileOrder;

  // ────────────────────────────────────────────────────────────────────────────────
  // Effects: Initialization
  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Grab email from AsyncStorage & load driver plan & get distance/time
  useEffect(() => {
    const getStorageInfo = async () => {
      const userEmail = await AsyncStorage.getItem('email');
      setStoredEmail(userEmail);
    };
    const fetchDriverPlan = async () => {
      dispatch(getDriverPlan());
    };

    // Request location permissions & user location
    const requestLocation = async () => {
      const uloc = await getUserLocation();
      setMyLocation(uloc);
    };

    // Distance/time fetchers
    const fetchDistance = async (loc: Location.LocationObjectCoords) => {
      if (renderCharger) {
        const dist = getMyDistanceFromCharger(loc, renderCharger);
        setMyDistanceFromCharger(dist.toString());
      }
    };
    const fetchDrivingTime = async (loc: Location.LocationObjectCoords) => {
      if (renderCharger?.uuid) {
        const t = await getMyDrivingTime(loc, renderCharger.uuid);
        setMyTimeFromCharger(t);
      }
    };

    requestLocation();
    getStorageInfo();
    fetchDriverPlan();

    if (route?.params.searchCharger === undefined) {
      // Pre-filled distance/time from navigation params
      setMyDistanceFromCharger(route?.params.myDistanceFromCharger!);
      setMyTimeFromCharger(route?.params.myTimeFromCharger!);

      if (!route?.params.myDistanceFromCharger) {
        // We need to recalc
        fetchDistance(myLocation!);
      }
      if (!route?.params.myTimeFromCharger) {
        fetchDrivingTime(myLocation!);
      }
    }

    if (session) {
      setEmail(session.email!);
    }
  }, []);

  // 2) Whenever session changes, re-grab stored email
  useEffect(() => {
    const syncStoredEmail = async () => {
      const userEmail = await AsyncStorage.getItem('email');
      setStoredEmail(userEmail);
      if (userEmail) setEmail(userEmail);
    };
    syncStoredEmail();
  }, [session]);

  // 3) Ensure fresh state when returning to screen
  useFocusEffect(
    React.useCallback(() => {
      // If there's no order when focusing, ensure we start with clean state
      // This handles the case where user returns after the cleanup has run
      if (!order) {
        // Reset schedule state to defaults
        const defaultStartTime = new Date();
        const defaultEndTime = new Date(defaultStartTime.getTime() + 12 * 60 * 60 * 1000);
        setScheduleStartTime(defaultStartTime);
        setScheduleEndTime(defaultEndTime);
        setSelectedMaxKwh(null);
        setScheduleSet(false);

        // Reset form state
        setGuestEmail(null);
        setSelectedOption('');
        setShowCreateAccount(false);
        setShowFeeTooltipVisible(false);
      }
    }, [order])
  );

  // 4) When navigating away, cancel order and reset schedule state
  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', async () => {
      const navState = navigation.getState();
      const nextRoute = navState.routes[navState.index];
      const nextName = nextRoute?.name;

      // Only maintain state when navigating to ChargingScreen (to preserve order during charging)
      if (nextName !== 'ChargerStation' && nextName !== 'ChargingScreen') {
        // Cancel any existing order (with payment intent)
        if (order) {
          try {
            if (Platform.OS === 'web') {
              await cancelOrder(webStripeOrder!.id);
            } else {
              await cancelOrder(mobileOrder!.id);
            }
          } catch (error) {
            console.error('Error canceling order during navigation:', error);
          }
        }

        // Reset schedule state
        const defaultStartTime = new Date();
        const defaultEndTime = new Date(defaultStartTime.getTime() + 12 * 60 * 60 * 1000);
        setScheduleStartTime(defaultStartTime);
        setScheduleEndTime(defaultEndTime);
        setSelectedMaxKwh(null);
        setScheduleSet(false);

        // Reset order state
        if (Platform.OS === 'web') {
          setWebOrder(null);
        } else {
          setMobileOrder(null);
        }

        // Reset other form state
        setGuestEmail(null);
        setSelectedOption('');
        setShowCreateAccount(false);
        setShowFeeTooltipVisible(false);
      }
    });
    return () => unsubscribe();
  }, [navigation, route?.name, order, webStripeOrder, mobileOrder]);

  // 5) Auto-scroll when "Create Account" appears
  useEffect(() => {
    if (showCreateAccount) {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }
  }, [showCreateAccount]);



  // ────────────────────────────────────────────────────────────────────────────────
  // Handlers exposed by this hook
  // ────────────────────────────────────────────────────────────────────────────────
  // Email + Create/Sign-in UI
  const validateEmail = (e: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(e);
  };

  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
  const handleEmailChange = React.useCallback((e: string) => {
    setGuestEmail(e);
    if (e.length > 0) setIsValidEmail(validateEmail(e));
    else setIsValidEmail(false);
  }, []);

  const handleOptionSelect = React.useCallback((val: string) => {
    setSelectedOption((p) => (p === val ? '' : val));
  }, []);
  
  const handleCreateAccount = React.useCallback(() => {
    setShowCreateAccount((p) => !p);
  }, []);
  
  const handleSignIn = React.useCallback(() => {
    bottomSheetModalRef.current?.present();
  }, []);

  // Schedule setters
  const setScheduleTimes = (
    start: Date,
    end?: Date | null,
    maxKwh?: number | null
  ) => {
    const now = new Date();
    if (start < now) setScheduleStartTime(now);
    else setScheduleStartTime(start);

    if (end) setScheduleEndTime(end);
    else setScheduleEndTime(new Date(start.getTime() + 12 * 60 * 60 * 1000));

    setSelectedMaxKwh(maxKwh ?? null);
    setScheduleSet(true);
  };
  const clearSchedule = () => {
    setScheduleSet(false);
    setScheduleEndTime(defaultEndTime);
    setSelectedMaxKwh(null);
  };

  // "Complete Checkout" logic (shared by web/mobile)
  const [checkoutButtonDisabled, setCheckoutButtonDisabled] = useState<boolean>(
    Platform.OS === 'web'
  );

  // Update checkout button disabled state based on payment data and email validation
  useEffect(() => {
    if (Platform.OS === 'web') {
      const shouldBeDisabled =
        !paymentData || (RoleService.isGuest(session) && !isValidEmail);
      setCheckoutButtonDisabled(shouldBeDisabled);
    }
  }, [paymentData, session?.role, isValidEmail]);

  const handleCheckoutButton = async (): Promise<void> => {
    if (Platform.OS === 'web') {
      if (checkoutButtonDisabled) {
        return;
      }
      setCheckoutButtonDisabled(true);
    }

    if (!session) {
      notificationService.sendNotification('error', 'Invalid Session');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }
    const currentEmail = RoleService.isGuest(session) ? guestEmail : session.email!;
    if (!currentEmail) {
      notificationService.sendNotification('error', 'Please enter your email');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }
    if (!order) {
      notificationService.sendNotification(
        'error',
        'Problem creating your order'
      );
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }

    const liveOrder = currentOrderRef.current ?? order; // prefer ref
    if (!liveOrder) {
      notificationService.sendNotification('error', 'Problem creating your order');
      if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
      return;
    }

    try {
      // 1) Update order email
      await updateOrderEmail(order.id, currentEmail);

      // 2) Create schedule if applicable
      let scheduleObj: any = null;
      if (selectedCharger?.can_schedule && selectedCharger?.uuid) {
        const payload: SendSchedule = {
          start_time: scheduleStartTime.toISOString(),
          end_time: scheduleEndTime.toISOString(),
          chargerId: selectedCharger.uuid,
          orderId: order.id,
          max_kwh: selectedMaxKwh,
        };
        const res = await setSchedule(payload);
        if (Array.isArray(res)) scheduleObj = res[0];
        else scheduleObj = res;

        if (scheduleObj && scheduleObj.message) {
          notificationService.sendNotification('error', "Error setting schedules", scheduleObj.message);
          return;
        }
      }

      // 3) Perform checkout
      const resp = await checkoutCharger(
        liveOrder.id,
        currentEmail,
        scheduleObj?.scheduleId ?? null
      );
      if (isSuccessful(resp.status)) {
        if (selectedCharger?.uuid) {
          dispatch(updateSelectedCharger(selectedCharger.uuid));
        }
        navigation.navigate('ChargingScreen', { order, scheduleObj });
      } else {
        // Extract specific error message from backend response
        const errorMessage = (resp.payload as any)?.message || 'Error checking out charger.';
        notificationService.sendNotification(
          'error',
          errorMessage,
          errorMessage,
        );
      }
    } catch (e: any) {
      console.error(e);
      notificationService.sendNotification(
        'error',
        `Error checking out: ${e instanceof Error ? e.message : String(e)}`
      );
    }
        if (Platform.OS === 'web') setCheckoutButtonDisabled(false);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Tooltip handlers
  // ────────────────────────────────────────────────────────────────────────────────
  const showFeeTooltip = () => setShowFeeTooltipVisible(true);
  const hideFeeTooltip = () => setShowFeeTooltipVisible(false);

  // ────────────────────────────────────────────────────────────────────────────────
  // Mobile Stripe hook (after handleCheckoutButton is defined)
  // ────────────────────────────────────────────────────────────────────────────────
  const mobileStripeResult =
    Platform.OS === 'web'
      ? { 
        openPaymentSheet: async () => {}, 
        isPaymentSheetInitialized: true, 
        reinitializePaymentSheet: async () => {},
        chargerInUse: false
      }
      : useStripeMobile({
          selectedCharger: renderCharger,
          onNewOrderCreated: onMobileOrderCreated,
          onPaymentSuccess: handleCheckoutButton,
        });

  const { openPaymentSheet, isPaymentSheetInitialized, reinitializePaymentSheet, chargerInUse: mobileChargerInUse } = mobileStripeResult;
  
  const currentOrderRef = useRef<OrderAttributes | null>(null);
  useEffect(() => {
    currentOrderRef.current = Platform.OS === 'web' ? webStripeOrder : mobileOrder;
  }, [webStripeOrder, mobileOrder]);

  const mobileReinitRef = useRef(reinitializePaymentSheet);
  useEffect(() => { mobileReinitRef.current = reinitializePaymentSheet; }, [reinitializePaymentSheet]);

  const lastInitForOrderId = useRef<string | null>(null);

  const setNewOrder = React.useCallback(async (next: OrderAttributes | null) => {
    if (!next) return;
  
    if (Platform.OS === 'web') {
      setWebOrder(next);
      currentOrderRef.current = next;
      reinitializeForNewOrder?.(next);
    } else {
      setMobileOrder(next);
      currentOrderRef.current = next;
  
      if (lastInitForOrderId.current !== next.id) {
        lastInitForOrderId.current = next.id;
        // PASS THE ORDER so mobile hook does not create another one
        await mobileReinitRef.current?.(next);
      }
    }
  }, [reinitializeForNewOrder]);
  
  // ────────────────────────────────────────────────────────────────────────────────
  // Payment sheet auto-recovery effect (after mobile hook is declared)
  // ────────────────────────────────────────────────────────────────────────────────
  // useEffect(() => {
  //   // If we're on mobile and have an order but payment sheet is not initialized, try to reinitialize
  //   if (Platform.OS !== 'web' && order && !isPaymentSheetInitialized) {
  //     //console.log('Detected uninitialized payment sheet with active order, attempting recovery...');
  //     const timer = setTimeout(() => {
  //       reinitializePaymentSheet();
  //     }, 1000); // Small delay to avoid rapid re-initialization
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [newOrder, isPaymentSheetInitialized, reinitializePaymentSheet]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Refresh charger data (for pull-to-refresh)
  // ────────────────────────────────────────────────────────────────────────────────
  const refreshChargerData = React.useCallback(async () => {
    if (!selectedCharger?.uuid) return;
    
    try {
      // Re-fetch the charger data to get updated status (including OCPP status)
      await dispatch(updateSelectedCharger(selectedCharger.uuid));
    } catch (error) {
      console.error('Error refreshing charger data:', error);
      notificationService.sendNotification(
        'error',
        'Failed to refresh charger data'
      );
    }
  }, [dispatch, selectedCharger?.uuid]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Expose everything to the screen
  // ────────────────────────────────────────────────────────────────────────────────
  return {
    // Common state
    email,
    guestEmail,
    storedEmail,
    selectedOption,
    showCreateAccount,
    myDistanceFromCharger,
    myTimeFromCharger,
    myLocation,

    // Orders + payments
    order,
    setNewOrder,
    paymentData: Platform.OS === 'web' ? paymentData : null,
    paymentsDropdownOpen: Platform.OS === 'web' ? paymentsDropdownOpen : false,
    loadError: Platform.OS === 'web' ? loadError : false,
    chargerInUse: Platform.OS === 'web' ? chargerInUse : mobileChargerInUse,
    stripeElements: Platform.OS === 'web' ? stripeElements : undefined,

    // Stripe handlers (web)
    onDropdownPressed: Platform.OS === 'web' ? onDropdownPressed : () => {},
    onPaymentSubmit: Platform.OS === 'web' ? onPaymentSubmit : async () => {},
    handleRemovePayment: Platform.OS === 'web' ? handleRemovePayment : () => {},

    // Stripe handlers (mobile)
    openPaymentSheet: Platform.OS !== 'web' ? openPaymentSheet : async () => {},
    isPaymentSheetInitialized: Platform.OS !== 'web' ? isPaymentSheetInitialized : true,
    reinitializePaymentSheet: Platform.OS !== 'web' ? reinitializePaymentSheet : async () => {},

    // Checkout
    checkoutButtonDisabled,
    handleCheckoutButton,

    // Schedule
    scheduleStartTime,
    scheduleEndTime,
    selectedMaxKwh,
    scheduleSet,
    setScheduleTimes,
    clearSchedule,

    // Tooltip
    showFeeTooltipVisible,
    showFeeTooltip,
    hideFeeTooltip,

    // Other UI state
    isValidEmail,
    activeCharges,
    session,
    selectedCharger,

    // Refs
    scrollViewRef,
    bottomSheetModalRef,

    // Shared UI handlers
    handleEmailChange,
    handleOptionSelect,
    handleCreateAccount,
    handleSignIn,

    // Refresh
    refreshChargerData,
  };
}
